import { useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore, useDashboardStore, useSubjectsStore } from '../lib/store';

/**
 * useRealtimeSync
 * 
 * Automatically synchronizes the local Zustand state with the Supabase Realtime engine.
 * Listens for changes to profiles, daily_stats, and subject_progress.
 * trg_auto_profile_stats and other triggers will fire on the DB, 
 * and this hook will ensure the UI reflects those changes immediately.
 */
export const useRealtimeSync = () => {
    const { profile, setProfile } = useAuthStore();
    const { fetchDashboard } = useDashboardStore();
    const { fetchAll: fetchSubjects } = useSubjectsStore();

    useEffect(() => {
        if (!profile?.id) return;

        // 1. Subscribe to profile updates
        const profileSub = supabase
            .channel(`profile-${profile.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'profiles',
                    filter: `id=eq.${profile.id}`
                },
                (payload) => {
                    console.log('Realtime profile update:', payload.new);
                    setProfile(payload.new as any);
                }
            )
            .subscribe();

        // 2. Subscribe to daily stats updates (triggers dashboard refresh)
        const statsSub = supabase
            .channel(`stats-${profile.id}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'daily_stats',
                    filter: `user_id=eq.${profile.id}`
                },
                () => {
                    console.log('Realtime stats update, refreshing dashboard...');
                    fetchDashboard();
                }
            )
            .subscribe();

        // 3. Subscribe to subject progress updates
        const progressSub = supabase
            .channel(`progress-${profile.id}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'subject_progress',
                    filter: `user_id=eq.${profile.id}`
                },
                () => {
                    console.log('Realtime progress update, refreshing subjects...');
                    fetchSubjects();
                }
            )
            .subscribe();

        return () => {
            profileSub.unsubscribe();
            statsSub.unsubscribe();
            progressSub.unsubscribe();
        };
    }, [profile?.id, setProfile, fetchDashboard, fetchSubjects]);
};
